/* security utilities for preventing sql injection and other attacks */

export function isSupabaseAnonKey(value: string): boolean {
  const parts = value.split('.')
  if (parts.length !== 3 || parts.some(part => !part)) return false
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    return payload?.role === 'anon'
  } catch {
    return false
  }
}

/*
 * sanitize user input for use in supabase ilike queries
 * escapes special characters that could break query structure
 * note: supabase's query builder handles parameterization, but we sanitize to be extra safe
 */
export function sanitizeForILIKE(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  // remove null bytes and control characters
  // eslint-disable-next-line no-control-regex
  let sanitized = input.replace(/[\x00-\x1F\x7F]/g, '')
  
  // remove or escape characters that could break the query structure
  // since we're using template strings in .or() calls, we need to be careful
  // remove characters that could break the ilike pattern syntax
  sanitized = sanitized
    .replace(/['"]/g, '')      // Remove quotes (could break string syntax)
    .replace(/;/g, '')         // Remove semicolons (SQL statement separator)
    .replace(/--/g, '')        // Remove SQL comment markers
    .replace(/\/\*/g, '')      // Remove block comment start
    .replace(/\*\//g, '')      // Remove block comment end
    .replace(/\\/g, '')        // Remove backslashes (escape characters)
    .replace(/%/g, '')         // Remove % (ILIKE wildcard - we add our own)
    .replace(/_/g, '')        // Remove _ (ILIKE single char wildcard)
  
  // Remove SQL keywords that could be used for injection
  // Only remove if they appear as standalone words (not part of other words)
  const sqlKeywords = /\b(union|select|insert|update|delete|drop|create|alter|exec|execute|script|truncate|grant|revoke)\b/gi
  sanitized = sanitized.replace(sqlKeywords, '')

  // Limit length to prevent DoS
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200)
  }

  return sanitized.trim()
}

/**
 * Validate SQL query to ensure it's safe (SELECT only, no dangerous operations)
 * Used for smart search queries
 */
export function validateSelectQuery(sql: string): { valid: boolean; error?: string } {
  if (!sql || typeof sql !== 'string') {
    return { valid: false, error: 'SQL query is required' }
  }

  const upperSql = sql.trim().toUpperCase()

  // must start with select
  if (!upperSql.startsWith('SELECT')) {
    return { valid: false, error: 'Only SELECT queries are allowed' }
  }

  // block dangerous sql keywords
  const dangerousKeywords = [
    'INSERT', 'UPDATE', 'DELETE', 'DROP', 'CREATE', 'ALTER', 'TRUNCATE',
    'EXEC', 'EXECUTE', 'EXECUTE IMMEDIATE', 'CALL', 'GRANT', 'REVOKE',
    '--', '/*', '*/', ';', 'UNION ALL', 'UNION'
  ]

  for (const keyword of dangerousKeywords) {
    if (upperSql.includes(keyword)) {
      return { valid: false, error: `Dangerous SQL keyword detected: ${keyword}` }
    }
  }

  // block semicolons (except at the end which we'll remove)
  if (upperSql.includes(';') && !upperSql.endsWith(';')) {
    return { valid: false, error: 'Semicolons are not allowed in queries' }
  }

  // must have limit clause to prevent large result sets
  if (!upperSql.includes('LIMIT')) {
    return { valid: false, error: 'Query must include LIMIT clause' }
  }

  // check limit value is reasonable (max 1000)
  const limitMatch = upperSql.match(/LIMIT\s+(\d+)/i)
  if (limitMatch) {
    const limitValue = parseInt(limitMatch[1], 10)
    if (limitValue > 1000) {
      return { valid: false, error: 'LIMIT value cannot exceed 1000' }
    }
  }

  return { valid: true }
}

/*
 * sanitize schema name to prevent sql injection
 * only allows alphanumeric characters and underscores, must start with letter
 */
export function sanitizeSchemaName(name: string): string {
  if (!name || typeof name !== 'string') {
    return ''
  }

  // remove all non-alphanumeric characters except underscores
  let sanitized = name.replace(/[^a-z0-9_]/gi, '')
  
  // must start with a letter
  if (!/^[a-z]/i.test(sanitized)) {
    sanitized = 'schema_' + sanitized
  }

  // limit length
  if (sanitized.length > 63) {
    sanitized = sanitized.substring(0, 63)
  }

  return sanitized.toLowerCase()
}

/* validate email address format */
export function validateEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

/* sanitize string for general use (remove dangerous characters) */
export function sanitizeString(input: string, maxLength: number = 1000): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  // remove null bytes and control characters
  // eslint-disable-next-line no-control-regex
  let sanitized = input.replace(/[\x00-\x1F\x7F]/g, '')
  
  // limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength)
  }

  return sanitized.trim()
}
